/**
 * Discount Preview Engine — Orchestrator
 *
 * Verwaltet aktive Rabattcodes und liefert Preview-Preise pro Variante via
 * Shopify Storefront API (ephemerer cartCreate, kein Eingriff in den echten
 * Kunden-Cart).
 *
 * Architektur (3 IIFE-Module, geladen in dieser Reihenfolge):
 *   1. discount-preview-state.js  — State, sessionStorage, getters
 *   2. discount-preview-api.js    — Storefront/Cart-Netzwerk, localStorage-Cache
 *   3. discount-preview.js        — Orchestrator (dieses File): applyCode,
 *                                    removeCode, reconcile, init, events
 *
 * State-Synchronisation:
 *   - cart.discount_codes  (Shopify-Wahrheit für Checkout)
 *   - sessionStorage       (UI-Wahrheit für Vorschau)
 *   - localStorage-Cache   (Preview-Preise pro Variant)
 *
 * Globale API: window.discountPreview
 *
 * Events (auf document.documentElement):
 *   discount:updated   { codes, previews, pendingCodes }     - nach Reconcile/Preview
 *   discount:error     { code, reason, message? }            - bei applyCode-Fehler
 *   discount:rejected  { code, reason }                      - wenn Shopify Code verworfen hat
 *
 *   reason-Werte für discount:error: 'cart_unreachable', 'invalid',
 *   'not_combinable', 'remove_failed'. message ist optional, dient nur als
 *   technischer Kontext für Debugging und sollte NICHT zur Branch-Logik
 *   in Listenern verwendet werden.
 */
(function () {
  'use strict';

  // ----------------------------------------------------------------------
  // Module-Imports via globale Namespaces (__dpState, __dpApi)
  // Die beiden Module sind via <script defer> VOR discount-preview.js geladen.
  // ----------------------------------------------------------------------
  const stateMod = window.__dpState;
  const apiMod = window.__dpApi;

  if (!stateMod || !apiMod) {
    console.error('[discount-preview] state oder api module nicht geladen — Skript-Reihenfolge prüfen.');
    return;
  }

  const state = stateMod.state;
  const readActiveCodes = stateMod.readActiveCodes;
  const writeActiveCodes = stateMod.writeActiveCodes;
  const getActiveCodes = stateMod.getActiveCodes;
  const getPendingCodes = stateMod.getPendingCodes;
  const getDisplayDiscounts = stateMod.getDisplayDiscounts;
  const deriveDisplayDiscounts = stateMod.deriveDisplayDiscounts;
  const hasActiveDiscounts = stateMod.hasActiveDiscounts;
  const deriveAutoDiscount = stateMod.deriveAutoDiscount;

  const isEnabled = apiMod.isEnabled;
  const readCache = apiMod.readCache;
  const cacheKey = apiMod.cacheKey;
  const invalidateCache = apiMod.invalidateCache;
  const fetchCart = apiMod.fetchCart;
  const pushCodeToCart = apiMod.pushCodeToCart;
  const fireCartChange = apiMod.fireCartChange;
  const fetchStorefrontPreview = apiMod.fetchStorefrontPreview;

  // ----------------------------------------------------------------------
  // Event-Dispatch
  // ----------------------------------------------------------------------
  function fireEvent(name, detail) {
    let payload = detail || {};
    // discount:updated bekommt immer die aktuelle pendingCodes-Liste mit,
    // damit der renderer toasts nur für ECHT applicable codes spawnt
    // (pending codes auf leerem cart würden sonst beim apply schon toasten).
    if (name === 'discount:updated' && !('pendingCodes' in payload)) {
      payload = Object.assign({}, payload, {
        pendingCodes: Array.from(state && state.pendingCodes ? state.pendingCodes : [])
      });
    }
    document.documentElement.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail: payload })
    );
  }

  /**
   * VIP-Baseline aus dem Kunden-Metafeld (via themeVariables, gesetzt in
   * js-variables.liquid). Greift wenn der echte Cart noch keinen Rabatt zeigt
   * (z.B. leerer Warenkorb), damit kundengebundene VIP-Rabatte schon beim
   * Browsen als Streichpreis erscheinen. Null wenn kein/0 Prozent.
   */
  function vipBaselineDiscount() {
    const pct = Number(apiMod.config && apiMod.config.vipDiscountPercent);
    if (!Number.isFinite(pct) || pct <= 0) return null;
    return { percent: pct, valueType: 'percentage', targetSelection: 'all', title: 'vip' };
  }

  /**
   * Leitet aus einem frischen Cart den automatischen Prozent-Rabatt ab und
   * legt ihn in state.autoDiscount ab. Wird bei jedem Cart-Fetch aufgerufen.
   * Fällt auf die VIP-Baseline zurück, wenn der Cart (noch) keinen Rabatt
   * trägt, damit die Preview auch bei leerem Warenkorb rechnet.
   */
  function syncAutoDiscount(cart) {
    const next = deriveAutoDiscount(cart) || vipBaselineDiscount();
    const prev = state.autoDiscount;
    const changed = (!!prev !== !!next) || (prev && next && prev.percent !== next.percent);
    state.autoDiscount = next;
    if (changed) invalidateCache();
  }

  // --- Pure preview math (no DOM) ------------------------------------------
  // Extracted from synthesizeAutoPreview so the money-critical clamping is unit
  // testable. Exposed via window.discountPreview._internal.

  // Clamps a percentage to 0..100. auto.percent stems from Shopify
  // (cart_level_discount_applications) or the VIP setting/metafield; a
  // misconfigured value >100 would otherwise yield a negative preview price.
  function clampPercent(percent) {
    return Math.min(100, Math.max(0, percent));
  }

  // Builds the synthetic preview entry for one variant from its base price and
  // an already-clamped percentage. Returns null when there is no base price or
  // when the discount would not actually lower the price. finalUnit is floored
  // at 0 so a negative amount is never displayed.
  function buildSyntheticEntry(id, baseCents, pct) {
    if (!baseCents) return null;
    const finalUnit = Math.max(0, Math.round((baseCents * (100 - pct)) / 100));
    if (finalUnit >= baseCents) return null;
    return {
      variantId: id,
      originalCents: baseCents,
      finalCents: finalUnit,
      finalLineCents: finalUnit,
      savingsCents: baseCents - finalUnit,
      percentageOff: Math.round(pct),
      discountCodes: []
    };
  }

  /**
   * Berechnet Preview-Preise für einen automatischen Prozent-Rabatt rein
   * clientseitig aus den im DOM hinterlegten Basispreisen (data-base-price).
   * Kein Storefront-Call: kundengebundene Rabatte (Smile-VIP) sind im anonymen
   * cartCreate ohnehin unsichtbar, und der Prozentsatz auf alle Items ist
   * deterministisch.
   */
  function synthesizeAutoPreview(numericIds) {
    const auto = state.autoDiscount;
    const result = {};
    if (!auto || auto.valueType !== 'percentage') return result;
    const pct = clampPercent(auto.percent);
    numericIds.forEach((id) => {
      const pl = document.querySelector('price-list[data-variant-id="' + id + '"]:not([data-context="line-item"])');
      const base = pl ? Number(pl.dataset.basePrice || 0) : 0;
      const entry = buildSyntheticEntry(id, base, pct);
      if (entry) result[id] = entry;
    });
    return result;
  }

  // ----------------------------------------------------------------------
  // Preview-Pipeline (Cache → schedule → fetch → cache)
  // ----------------------------------------------------------------------

  /**
   * Liefert PriceInfo für eine Liste Variant-IDs.
   * Cache-Hit synchron, Cache-Miss triggert API-Call.
   */
  function getPreview(variantIds) {
    if (!isEnabled || !hasActiveDiscounts()) return {};

    const numericIds = variantIds.map(Number).filter((n) => Number.isFinite(n) && n > 0);
    if (!numericIds.length) return {};

    // Auto-Rabatt-Fall (kein Code): Preise clientseitig aus dem Prozentsatz
    // rechnen, kein Storefront-Roundtrip. Codes haben Vorrang und laufen
    // weiter über die anonyme Storefront-API (dort funktionieren sie).
    if (!state.codes.length && state.autoDiscount) {
      return synthesizeAutoPreview(numericIds);
    }

    const cache = readCache();
    const result = {};
    const missing = [];

    numericIds.forEach((id) => {
      const key = cacheKey(state.codes, [id]);
      if (cache[key] && cache[key][id]) {
        result[id] = cache[key][id];
      } else {
        missing.push(id);
      }
    });

    if (missing.length) {
      schedulePreviewFetch(missing);
    }

    return result;
  }

  let fetchScheduled = false;
  function schedulePreviewFetch(variantIds) {
    variantIds.forEach((id) => state.pendingVariants.add(id));
    if (fetchScheduled) return;
    fetchScheduled = true;
    // Microtask statt setTimeout(50ms): alle synchronen getPreview-Calls im
    // selben Tick werden gebatcht (Renderer ruft getPreview pro price-list,
    // alle landen via pendingVariants im selben Fetch). Microtask fires
    // unmittelbar nach Sync-Code, daher kein wahrnehmbares Delay.
    Promise.resolve().then(runPendingFetch);
  }

  async function runPendingFetch() {
    fetchScheduled = false;
    if (!state.codes.length) {
      state.pendingVariants.clear();
      return;
    }
    const variantIds = Array.from(state.pendingVariants);
    state.pendingVariants.clear();
    if (!variantIds.length) return;

    const result = await fetchStorefrontPreview(variantIds, state.codes);
    if (!result) return;

    const cache = readCache();
    // Nur Per-Variant-Entries persistieren, kein zusaetzlicher Batch-Entry.
    // Die Batch-Variante war redundant zu den Single-Entries (jeder getPreview-
    // Lookup laeuft sowieso pro Variant) und hat den Cache pro Fetch verdoppelt
    // — Hauptursache fuer QuotaExceededError bei Fremd-Scripts wie Elevar.
    // delete-then-set rotiert aktive Eintraege ans Ende der Insertion-Order,
    // damit das FIFO-Trimming in writeCache faktisch LRU-aehnlich evictet.
    Object.keys(result.previews).forEach((vidKey) => {
      const vidNum = parseInt(vidKey, 10);
      if (!Number.isFinite(vidNum)) return;
      const singleKey = cacheKey(state.codes, [vidNum]);
      delete cache[singleKey];
      cache[singleKey] = { [vidKey]: result.previews[vidKey] };
    });
    apiMod.writeCache(cache);

    // Der Storefront-Preview läuft gegen einen ANONYMEN, isolierten
    // Single-Variant-cartCreate und ist NICHT autoritativ dafür, welche Codes
    // im echten Kunden-Cart applicable sind. Conditional/customer/automatic
    // Rabatte (z.B. die NATURE_G2-BOGO: "kauf G2 → Gratis-Zahnpasta + 30% auf
    // die Bürste") reproduzieren in diesem Cart bewusst nicht und fehlen daher
    // in result.appliedCodes, obwohl sie real aktiv sind. Den Preview-Pfad
    // also strikt read-only halten: KEIN discount:rejected feuern und
    // state.codes NICHT überschreiben. Echte Code-Konflikte und verworfene
    // Codes werden gegen den echten Cart in reconcile() (discount_codes) und
    // applyCodeInner() erkannt. Das ist die einzige Wahrheit. Frühere
    // Versionen leiteten hier ein "not_combinable" ab und wischten state.codes,
    // was eine falsche Fehlermeldung und ein doppeltes Toast-Popup auslöste
    // (siehe Issue #266).

    fireEvent('discount:updated', {
      codes: state.codes,
      previews: result.previews,
      variantIds: variantIds
    });
  }

  // ----------------------------------------------------------------------
  // Concurrency-Mutex: applyCode/removeCode ändern beide state.codes und
  // feuern /cart/update.js. Concurrent Aufrufe würden state.codes
  // inkonsistent halten. Wir serialisieren über eine Promise-Queue.
  // ----------------------------------------------------------------------
  let codeOpQueue = Promise.resolve();
  function serializeCodeOp(fn) {
    const next = codeOpQueue.then(() => fn(), () => fn());
    codeOpQueue = next.catch(() => {});
    return next;
  }

  /**
   * Prüft via Storefront-API-cartCreate ob ein Code IN ISOLATION applicable
   * wäre (frischer Cart, nur dieser eine Code). Wird zur Differenzierung von
   * "ungültig" vs "konfliktiert mit bestehenden Codes" verwendet.
   * Liefert true (valid), false (invalid), null (Test nicht möglich).
   */
  async function isCodeValidInIsolation(code) {
    try {
      // Brauchen mindestens eine Variant-ID. Wir nehmen die erste auf der
      // Seite sichtbare price-list, das deckt Collection/PDP/Cart ab.
      const pl = document.querySelector('price-list[data-variant-id]');
      const vidRaw = pl && pl.getAttribute('data-variant-id');
      const vid = vidRaw && parseInt(vidRaw, 10);
      if (!vid) return null;
      const result = await fetchStorefrontPreview([vid], [code]);
      if (!result) return null;
      return result.appliedCodes.some((c) => c.toLowerCase() === code.toLowerCase());
    } catch (e) {
      return null;
    }
  }

  // ----------------------------------------------------------------------
  // applyCode — Code hinzufügen
  // ----------------------------------------------------------------------

  /** Code hinzufügen: an Cart pushen, sessionStorage updaten, Preview triggern. */
  async function applyCode(rawCode) {
    return serializeCodeOp(() => applyCodeInner(rawCode));
  }

  async function applyCodeInner(rawCode) {
    const code = String(rawCode || '').trim();
    if (!code) return { ok: false, reason: 'empty' };

    if (state.codes.includes(code)) {
      return { ok: true, alreadyActive: true };
    }

    // /cart/update.js mit nur dem neuen Code würde die bestehenden Codes
    // ersetzen statt kombinieren. Wir senden daher die FULL-Liste der schon
    // aktiven Codes plus den neuen, komma-getrennt. Shopify entscheidet
    // anhand der combines_with-Config welche dann applicable bleiben.
    const hadExistingCodes = state.codes.length > 0;
    const wantedCodes = state.codes.concat([code]);
    const result = await pushCodeToCart(wantedCodes.join(','));

    // /cart/update.js gibt immer 200 zurück, auch wenn der Code ungültig ist.
    // Wir müssen cart.discount_codes lesen um zu sehen ob Shopify ihn akzeptiert hat.
    const cart = result.ok ? result.cart : await fetchCart();
    if (!cart) {
      fireEvent('discount:error', { code: code, reason: 'cart_unreachable', message: 'cart_fetch_failed' });
      return { ok: false, reason: 'cart_unreachable' };
    }

    const accepted = (cart.discount_codes || []).find(
      (c) => c.code && c.code.toLowerCase() === code.toLowerCase() && c.applicable
    );

    const newApplicable = (cart.discount_codes || [])
      .filter((c) => c.applicable)
      .map((c) => c.code);

    // displayDiscounts immer aus dem frischen cart aktualisieren, damit
    // chips sowohl code- als auch automatic-discounts widerspiegeln.
    state.displayDiscounts = deriveDisplayDiscounts(cart);
    syncAutoDiscount(cart);

    // Codes die wir VORHER hatten aber Shopify jetzt verworfen hat.
    // Wenn der neue Code akzeptiert wurde, ist das Drop erwartet ("besserer
    // Code gewinnt") — kein Fehler-Event, sonst flackert die UI mit einer
    // Fehlermeldung obwohl der Apply tatsächlich erfolgreich war. Nur wenn
    // der neue Code ABGELEHNT wurde und alte Codes trotzdem rausgeflogen
    // sind, ist das ein echter Konflikt für den User.
    if (!accepted) {
      state.codes.forEach((prev) => {
        if (!newApplicable.includes(prev) && prev.toLowerCase() !== code.toLowerCase()) {
          fireEvent('discount:rejected', { code: prev, reason: 'not_combinable' });
        }
      });
    }

    if (!accepted) {
      // /cart/update.js gibt für "ungültig", "nicht kombinierbar" UND
      // "Cart leer, Code wartet auf Items" dieselbe Response: applicable:false.
      const cartIsEmpty = !cart.items || cart.items.length === 0;

      // Cart-Leer-Fall, noch keine Codes aktiv: Code als pending speichern.
      // Wir verzichten hier bewusst auf die Storefront-API-Isolation, weil sie
      // ohne customerAccessToken keine customer-only Codes erkennt und sonst
      // valide Codes (z.B. Freundes-Codes wie DOMINIKFRIENDS) fälschlich als
      // ungültig melden würde. Beim ersten Add-to-Cart triggert reconcile()
      // gegen den echten Cart (mit Customer-Cookie) und überschreibt state
      // falls Shopify den Code dort tatsächlich verwirft.
      if (cartIsEmpty && !hadExistingCodes) {
        state.codes = wantedCodes;
        state.pendingCodes.add(code);
        writeActiveCodes(state.codes);
        invalidateCache();
        fireEvent('discount:updated', {
          codes: state.codes,
          pendingCodes: getPendingCodes(),
          previews: {}
        });
        await fireCartChange(result.sections);
        return { ok: true, code: code, pending: true };
      }

      // Cart hat Items (oder bereits Codes): Storefront-Isolation hilft uns
      // zu entscheiden zwischen "ungültig" und "nicht kombinierbar". Bei
      // customer-only Codes ist Isolation null/false; das führt zu "invalid"
      // statt "not_combinable" — bewusst akzeptiert, weil wir den unterschied
      // ohne customerAccessToken nicht zuverlässig ermitteln können.
      let isolatedValid = false;
      if (isEnabled) {
        isolatedValid = (await isCodeValidInIsolation(code)) === true;
      }
      const reason = isolatedValid && hadExistingCodes ? 'not_combinable' : 'invalid';
      fireEvent('discount:error', { code: code, reason: reason });
      // state.codes trotzdem aktualisieren falls Shopify die Liste umkonfiguriert
      // (z.B. neuer Code wurde verworfen, aber alte Codes sind unverändert)
      state.codes = newApplicable;
      writeActiveCodes(state.codes);
      fireEvent('discount:updated', { codes: state.codes, previews: {} });
      return { ok: false, reason: reason };
    }

    // Sync mit cart.discount_codes (Shopify-Wahrheit)
    state.codes = newApplicable;
    writeActiveCodes(state.codes);

    // Cache invalidieren, da sich Code-Set geändert hat
    invalidateCache();

    fireEvent('discount:updated', { codes: state.codes, previews: {} });

    // Cart-Drawer + Main-Cart neu rendern lassen
    await fireCartChange(result.sections);

    return { ok: true, code: accepted.code };
  }

  // ----------------------------------------------------------------------
  // removeCode — Code entfernen
  // ----------------------------------------------------------------------

  /** Code entfernen: aus sessionStorage, Cart-Code-Liste rekonstruieren. */
  async function removeCode(rawCode) {
    return serializeCodeOp(() => removeCodeInner(rawCode));
  }

  async function removeCodeInner(rawCode) {
    const code = String(rawCode || '').trim();
    if (!code) return { ok: false, reason: 'empty' };

    const previousCodes = state.codes.slice();
    const remaining = state.codes.filter((c) => c.toLowerCase() !== code.toLowerCase());

    // OPTIMISTIC UPDATE: state sofort umstellen, UI reagiert ohne auf den
    // Netz-Roundtrip zu warten. Bei Failure rollen wir zurück.
    state.codes = remaining;
    state.pendingCodes.delete(code);
    writeActiveCodes(state.codes);
    invalidateCache();
    fireEvent('discount:updated', { codes: state.codes, previews: {} });

    // Netzwerk-Call im Hintergrund.
    let lastResult;
    try {
      lastResult = await pushCodeToCart(remaining.join(','));
      if (!lastResult || !lastResult.ok) throw new Error('cart_update_failed');
    } catch (err) {
      // ROLLBACK: state zurück auf previousCodes
      state.codes = previousCodes;
      writeActiveCodes(state.codes);
      invalidateCache();
      fireEvent('discount:updated', { codes: state.codes, previews: {} });
      fireEvent('discount:error', { code: code, reason: 'remove_failed' });
      return { ok: false, reason: 'remove_failed' };
    }

    // Erfolg: mit Shopify-Wahrheit reconcilen (kann sich von "remaining"
    // unterscheiden wenn Shopify Codes hinzufügt oder entfernt hat).
    //
    // SHOPIFY-LIMITATION: /cart/update.js mit discount:"" kann unter manchen
    // Konfigurationen Codes nicht zuverlässig clearen (insb. bei leerem
    // Cart oder wenn der Code als customer/automatic-discount markiert ist).
    // Reconcile fängt das ab: wenn der gerade entfernte Code in
    // discount_codes wieder auftaucht, fireEvent('discount:rejected', ...)
    // damit das chip-input-UI dem Kunden einen Hinweis zeigen kann.
    const cart = await fetchCart();
    if (cart) {
      const serverApplicable = (cart.discount_codes || [])
        .filter((c) => c.applicable)
        .map((c) => c.code);
      state.displayDiscounts = deriveDisplayDiscounts(cart);
      syncAutoDiscount(cart);
      if (serverApplicable.join('|') !== state.codes.join('|')) {
        state.codes = serverApplicable;
        writeActiveCodes(state.codes);
        fireEvent('discount:updated', { codes: state.codes, previews: {} });
      }
      const stillPresent = serverApplicable.some(
        (c) => c.toLowerCase() === code.toLowerCase()
      );
      if (stillPresent) {
        fireEvent('discount:rejected', { code: code, reason: 'cannot_remove' });
      }
    }

    // Cart-Drawer + Main-Cart re-rendern. Das ist der einzige nicht-
    // optimistische Teil — Cart-Totals brauchen die Server-HTML.
    await fireCartChange(lastResult.sections);
    return { ok: true };
  }

  // ----------------------------------------------------------------------
  // reconcile — sessionStorage gegen cart.discount_codes abgleichen
  // ----------------------------------------------------------------------

  /** Reconcile: cart.discount_codes ist die Wahrheit, sessionStorage angleichen. */
  async function reconcile() {
    const cart = await fetchCart();
    if (!cart) return;

    const cartCodes = (cart.discount_codes || [])
      .filter((c) => c.applicable)
      .map((c) => c.code);

    // displayDiscounts aktualisieren (codes + automatic) damit chips beide
    // Typen zeigen — auch wenn nur die codes in state.codes landen.
    state.displayDiscounts = deriveDisplayDiscounts(cart);
    // Automatischen Prozent-Rabatt aus dem echten Cart ableiten (z.B. Smile-VIP
    // 30%): triggert die clientseitige Preview obwohl kein Code aktiv ist.
    syncAutoDiscount(cart);

    // Cart-Leer-Fall: Shopify kann keine Codes als applicable markieren wenn
    // keine Lines da sind. Unser state.codes hält aber den User-Intent (z.B.
    // URL-Code beim Landing auf leerer Cart). Nicht überschreiben, sonst
    // verlieren wir den Code bevor der User überhaupt was hinzufügen konnte.
    // Renderer zeigt Preview-Preise via Storefront-API trotzdem korrekt an.
    //
    // WICHTIG (robustes /discount/CODE-Handling): Ein /discount/ALL10-Link
    // setzt serverseitig einen Cookie und legt den Code in cart.discount_codes
    // ab, auf leerem Cart aber mit applicable:false. Der applicable-Filter oben
    // wirft solche Codes weg, und Shopify haengt KEIN ?discount= an den Redirect
    // (verifiziert), also greift parseUrlDiscount() auch nicht. Ohne den Merge
    // hier wuerde der Code erst sichtbar wenn zufaellig das erste Produkt im
    // Cart liegt. Darum: alle in cart.discount_codes gehaltenen Codes
    // (unabhaengig von applicable) in state.codes uebernehmen und als pending
    // markieren. Beim ersten Item macht Shopify sie automatisch applicable,
    // dann wandern sie via non-empty-Branch aus pendingCodes raus und der
    // Toast spawnt. Ungueltige Codes liefern via Storefront-Preview einfach
    // keinen Rabatt (graceful) und werden beim ersten Item wieder verworfen.
    //
    // Pending-Marker auf alle state.codes setzen: auf leerem Cart ist KEIN
    // Code wirklich applicable, also nie "Rabattcode aktiv" Toast spawnen.
    // Wichtig nach Page-Reload: state.codes kommt aus sessionStorage, aber
    // state.pendingCodes wird beim init neu mit leerem Set initialisiert.
    // Ohne diesen Marker würde der Renderer die Codes faelschlich als
    // applicable behandeln und den Toast zeigen.
    if (!cart.items || cart.items.length === 0) {
      const heldCodes = (cart.discount_codes || [])
        .map((c) => c.code)
        .filter(Boolean);
      const merged = [];
      state.codes.concat(heldCodes).forEach((c) => {
        if (!merged.some((m) => m.toLowerCase() === c.toLowerCase())) {
          merged.push(c);
        }
      });
      const changed = merged.join('|') !== state.codes.join('|');
      state.codes = merged;
      if (changed) {
        writeActiveCodes(merged);
        invalidateCache();
      }
      merged.forEach((c) => state.pendingCodes.add(c));
      fireEvent('discount:updated', {
        codes: state.codes,
        pendingCodes: getPendingCodes(),
        previews: {}
      });
      return;
    }

    const previous = state.codes;
    state.codes = cartCodes;
    writeActiveCodes(cartCodes);

    // Pending → applicable Transition: codes die als pending vorgemerkt waren
    // und jetzt im echten cart applicable sind, raus aus pendingCodes. Damit
    // sieht der renderer sie als "frisch aktiviert" und kann den toast spawnen.
    const cartCodesLc = cartCodes.map((c) => c.toLowerCase());
    Array.from(state.pendingCodes).forEach((p) => {
      if (cartCodesLc.includes(p.toLowerCase())) state.pendingCodes.delete(p);
    });

    // Wenn Code aus sessionStorage entfernt wurde (Shopify hat ihn nicht akzeptiert)
    const dropped = previous.filter((c) => !cartCodes.includes(c));
    dropped.forEach((c) => fireEvent('discount:rejected', { code: c, reason: 'not_in_cart' }));

    const changed = previous.join('|') !== cartCodes.join('|');
    if (changed) {
      invalidateCache();
    }
    // Event in JEDEM reconcile feuern: chips müssen auch dann erscheinen
    // wenn sich nur die displayDiscounts (automatic discounts) geändert
    // haben oder wenn der listener noch nichts gerendert hat (snippet kann
    // beim ersten cart:change schon im DOM sein, war es zur init-Zeit aber
    // ggf. nicht — section-replace etc.).
    fireEvent('discount:updated', {
      codes: state.codes,
      pendingCodes: getPendingCodes(),
      previews: {}
    });
  }

  // ----------------------------------------------------------------------
  // URL-Codes + Prefetch
  // ----------------------------------------------------------------------

  function parseUrlDiscount() {
    try {
      const params = new URLSearchParams(window.location.search);
      const codes = params.getAll('discount').filter(Boolean);
      if (!codes.length) return [];

      // URL bereinigen ohne neuen History-Eintrag
      params.delete('discount');
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname +
        (newSearch ? '?' + newSearch : '') +
        window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      return codes;
    } catch (e) {
      return [];
    }
  }

  /**
   * Eager Prefetch: scannt das DOM nach allen sichtbaren Variant-IDs und
   * triggert SOFORT den Storefront-API-Call. Läuft parallel zu reconcile,
   * statt erst zu warten bis Renderer-Init die Variants entdeckt. Spart die
   * Renderer-Init-Latenz, weil der Cache schon warm ist wenn refresh läuft.
   */
  function eagerPrefetch() {
    if (!isEnabled || !state.codes.length) return;
    const variantIds = new Set();
    document.querySelectorAll('price-list[data-variant-id], on-sale-badge[data-variant-id], [data-discount-overlay][data-variant-id]').forEach((el) => {
      const id = parseInt(el.dataset.variantId, 10);
      if (Number.isFinite(id) && id > 0) variantIds.add(id);
    });
    if (!variantIds.size) return;
    const idsArray = Array.from(variantIds);
    const cache = readCache();
    // Nur die ungecacheten Variants fetchen
    const missing = idsArray.filter((id) => {
      const key = cacheKey(state.codes, [id]);
      return !(cache[key] && cache[key][id]);
    });
    if (missing.length) {
      schedulePreviewFetch(missing);
    }
  }

  /**
   * Hover-Prefetch: wenn der User über einen Produkt-Link hovert, fetchen
   * wir die Variant der zugehörigen Product-Card. Beim Klick auf die PDP
   * ist die Variant dann schon im localStorage-Cache, instant Overlay.
   */
  function bindHoverPrefetch() {
    if (!isEnabled) return;
    let hoverDebounce = null;
    document.addEventListener('mouseover', (e) => {
      if (!state.codes.length) return;
      const link = e.target && e.target.closest && e.target.closest('a[href*="/products/"]');
      if (!link) return;
      // Suche Variant-ID in der Card-Umgebung des Links
      const card = link.closest('product-card, .product-card, article, .card');
      const pl = (card || link.parentElement || document).querySelector('price-list[data-variant-id]');
      if (!pl) return;
      const id = parseInt(pl.dataset.variantId, 10);
      if (!Number.isFinite(id)) return;
      // Cache prüfen, sonst prefetchen
      const cache = readCache();
      const key = cacheKey(state.codes, [id]);
      if (cache[key] && cache[key][id]) return;
      clearTimeout(hoverDebounce);
      hoverDebounce = setTimeout(() => {
        schedulePreviewFetch([id]);
      }, 80);
    }, { passive: true });
  }

  // ----------------------------------------------------------------------
  // Init + Event-Wiring
  // ----------------------------------------------------------------------

  async function init() {
    state.codes = readActiveCodes();
    // VIP-Baseline sofort setzen (synchron, vor jedem Cart-Fetch), damit der
    // Renderer schon beim ersten Paint mit leerem Warenkorb rabattiert.
    state.autoDiscount = vipBaselineDiscount();

    if (!isEnabled) {
      await reconcile();
      // Initiales event auch hier feuern, damit chip-renderer
      // (discount-code-input snippet) die aktiven codes anzeigt.
      fireEvent('discount:updated', { codes: state.codes, previews: {} });
      return;
    }

    // Eager Prefetch SOFORT starten, parallel zu applyCode/reconcile.
    // Macht den Cache warm bevor der Renderer überhaupt refresh läuft.
    eagerPrefetch();
    bindHoverPrefetch();

    const urlCodes = parseUrlDiscount();
    for (const code of urlCodes) {
      await applyCode(code);
    }

    await reconcile();

    // Nach reconcile state.codes evtl. geändert (URL-Code oder cart sync).
    // Nochmal prefetchen falls neue Codes aktiv sind die im ersten Lauf
    // noch nicht da waren.
    eagerPrefetch();

    // Immer EIN initiales discount:updated feuern, damit Listener (z.B.
    // discount-code-input snippet) die Chip-Liste rendern können. Ohne
    // dieses Event würden Codes, die schon vor Page-Load aktiv waren und
    // sich beim reconcile nicht ändern, gar nicht angezeigt (snippet
    // rendert beim Inline-Script-Lauf bevor discountPreview existiert).
    fireEvent('discount:updated', { codes: state.codes, previews: {} });
  }

  // Hook into cart-change-Events, damit nach AddToCart der Cart-State stimmt.
  // reconcile muss durch die codeOpQueue laufen, damit es nicht parallel zu
  // einem laufenden applyCode/removeCode state.codes überschreibt (z.B. wenn
  // upsell-manager während einer Code-Aktion ein cart:change feuert).
  document.documentElement.addEventListener('cart:change', () => {
    serializeCodeOp(() => reconcile());
  });

  window.discountPreview = {
    enabled: isEnabled,
    init: init,
    applyCode: applyCode,
    removeCode: removeCode,
    reconcile: reconcile,
    getActiveCodes: getActiveCodes,
    getDisplayDiscounts: getDisplayDiscounts,
    getPendingCodes: getPendingCodes,
    getPreview: getPreview,
    hasActiveDiscounts: hasActiveDiscounts,
    // Aktueller automatischer Prozent-Rabatt (oder null). Konsumenten ohne
    // eigene price-list-Elemente (z.B. upsell-manager) rechnen damit selbst.
    getAutoDiscount: function () {
      return state.autoDiscount ? Object.assign({}, state.autoDiscount) : null;
    },
    // Internal pure helpers exposed for unit tests only.
    _internal: {
      clampPercent: clampPercent,
      buildSyntheticEntry: buildSyntheticEntry,
      vipBaselineDiscount: vipBaselineDiscount
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
